import { useMemo, useState } from 'react';
import pluralize from 'pluralize';

import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import {
  ClassAnswerNoStudents,
  ClassAnswerNoAnswers,
} from '../class-answer-empty';
import { useGetClassStudents } from '../classroom-student.hooks';
import { prepareResultsPerStudent } from './interactive-response-individual-responses.utils';
import InteractiveResponseAttempts from './interactive-response-attempts';
import { Box } from 'approot/shared/design-system/box/box.style';
import type { InteractiveResponseAnswerFragment } from '../../present/activity-answers.graphql.generated';
import {
  ComponentInteractiveResponseCategorise,
  ComponentInteractiveResponseSequence,
} from 'approot/activity-builder/components/component.constants';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import ClassroomAnswerStudentVisibility from '../classroom-answer-student-visibility';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
  ClassroomAnswerInstruction,
} from '../classroom-answer.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import { ClassroomStudentIdentifierWithVisibility } from '../classroom-student-identifier';
import ActivityBuilderComponentMeasuredPreview from 'approot/activity-builder/page/activity-builder-component-sizer';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import { useGetStudentAnswersSummaryPollingFallback } from '../use-get-student-answers-summary';

type Props = {
  componentId: string;
  component: Omit<
    | ComponentInteractiveResponseSequence
    | ComponentInteractiveResponseCategorise,
    '__typename'
  >;
  renderAttempt: ({
    selectedAnswers,
  }: {
    selectedAnswers?: InteractiveResponseAnswerFragment;
  }) => JSX.Element;
};

const InteractiveResponseIndividualResponses = ({
  componentId,
  component,
  renderAttempt,
}: Props) => {
  const [studentDetailsId, setStudentDetailsId] = useState<number | null>();

  const { userClassId, contentId, contentType, version } =
    useActivityBuilderPresentContext();

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const { data: summaryData, error: summaryError } =
    useGetStudentAnswersSummaryPollingFallback({
      userClassId,
      componentId,
      contentId,
      contentType,
      contentVersion: version!,
    });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const lastStudentResponses = useMemo(
    () => summaryData?.getClassStudentsAnswersSummaryPerComponent || [],
    [summaryData]
  );

  const results = useMemo(() => {
    return prepareResultsPerStudent({
      studentsInClass,
      lastStudentResponses,
    });
  }, [studentsInClass, lastStudentResponses]);

  const studentDetails = useMemo(
    () => results.find(result => result.studentId === studentDetailsId),
    [results, studentDetailsId]
  );

  if (studentDetails) {
    return (
      <ActivitySheetInnerMax>
        <InteractiveResponseAttempts
          component={component}
          student={studentDetails}
          studentsInClass={studentsInClass}
          onSelect={setStudentDetailsId}
          renderAttempt={renderAttempt}
        />
      </ActivitySheetInnerMax>
    );
  }

  return (
    <ClassroomAnswerInstruction>
      {studentsLoading || !summaryData ? (
        <Bouncer />
      ) : summaryError || studentsError ? (
        <ErrorMessageActionable
          ariaPage="Individual Responses"
          paddingTop={0}
        />
      ) : studentsInClass.length === 0 ? (
        <ClassAnswerNoStudents />
      ) : lastStudentResponses.length === 0 ? (
        <ClassAnswerNoAnswers />
      ) : (
        <SubActivityView subActivityDetails={component.subActivityProperties}>
          <Box $justifyContent="flex-end" $marginBottom={24}>
            <ClassroomAnswerStudentVisibility />
          </Box>
          <ClassroomAnswerGrid>
            {results.map(result => (
              <ClassroomAnswerGridItem key={result.studentId}>
                <ClassroomAnswerGridInner>
                  {result.answer ? (
                    <>
                      <ClassroomAnswerGridButton
                        type="button"
                        onClick={() => setStudentDetailsId(result.studentId)}
                      >
                        <ScreenReaderOnly>
                          {`View ${result.studentName}'s response`}
                        </ScreenReaderOnly>
                      </ClassroomAnswerGridButton>
                      <ActivityBuilderComponentMeasuredPreview
                        id={result.studentId}
                        component={
                          <>
                            {renderAttempt({
                              selectedAnswers: result.answer,
                              ...component,
                            })}
                          </>
                        }
                      />
                    </>
                  ) : (
                    <ClassroomAwaitingResponse />
                  )}
                </ClassroomAnswerGridInner>
                <ClassroomStudentIdentifierWithVisibility
                  name={result.studentName}
                  emoji={result.studentEmoji}
                  secondaryText={
                    result.attempts > 0
                      ? `${result.attempts} ${pluralize(
                          'attempt',
                          result.attempts
                        )}`
                      : undefined
                  }
                />
              </ClassroomAnswerGridItem>
            ))}
          </ClassroomAnswerGrid>
        </SubActivityView>
      )}
    </ClassroomAnswerInstruction>
  );
};

export default InteractiveResponseIndividualResponses;
